import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { DEFAULT_FIELD_CONFIG } from "@/lib/fields";

const FieldsContext = createContext(null);
export const useFields = () => useContext(FieldsContext);

const localKey = (uid) => `smartscan_fields_${uid || "anon"}`;

export const FieldsProvider = ({ children }) => {
  const { user } = useAuth();
  const uid = user?.user_id;
  const [config, setConfigState] = useState(DEFAULT_FIELD_CONFIG);
  const [loaded, setLoaded] = useState(false);
  const saveTimer = useRef(null);

  // Load config when the user becomes available
  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    try {
      const cached = localStorage.getItem(localKey(uid));
      if (cached) setConfigState(JSON.parse(cached));
    } catch {
      /* ignore */
    }
    axios
      .get(`${API}/settings/fields`)
      .then((r) => {
        if (!cancelled) {
          setConfigState(r.data);
          setLoaded(true);
        }
      })
      .catch(() => setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const persist = useCallback(
    (next) => {
      try {
        localStorage.setItem(localKey(uid), JSON.stringify(next));
      } catch {
        /* ignore */
      }
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        axios.put(`${API}/settings/fields`, next).catch(() => {});
      }, 500);
    },
    [uid]
  );

  // Optimistic update: apply immediately (on the fly), debounce the network save.
  const updateConfig = useCallback(
    (updater) => {
      setConfigState((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        persist(next);
        return next;
      });
    },
    [persist]
  );

  return (
    <FieldsContext.Provider value={{ config, setConfig: updateConfig, loaded }}>
      {children}
    </FieldsContext.Provider>
  );
};
